// ============================================================================
// ROLE-BASED PERMISSIONS CONFIG — PRIMA
// Maps Keycloak roles to visible modules and allowed actions per module.
// Used at login time to filter navigation menu and module configs.
//
// The DB (admin.tbl_role_permissions / by-roles endpoint) is the SOLE source
// of truth for permissions. This file deliberately contains NO hardcoded
// permission map / fallback — login fails closed when the DB returns nothing
// (see login-interface.tsx). It provides only role constants and pure helpers.
// ============================================================================

export interface RoleMenuConfig {
  visibleModules: string[];
  moduleActions: Record<string, string[]>;
}

// Keycloak role constants for PRIMA
export const ROLES = {
  SYSTEM:   'prima_system',
  ADMIN:    'prima_admin',
  MANAGER:  'prima_manager',
  PARTNER:  'prima_partner',
  EMPLOYEE: 'prima_employee',
} as const;

export type AppRole = (typeof ROLES)[keyof typeof ROLES];

// Priority order — highest privilege first
export const ROLE_PRIORITY: AppRole[] = [
  ROLES.SYSTEM,
  ROLES.ADMIN,
  ROLES.MANAGER,
  ROLES.PARTNER,
  ROLES.EMPLOYEE,
];

// --------------------------------------------------------------------------
// Helper: pick the highest-priority PRIMA client role from a JWT's
// resource_access.prima.roles array (the client-based roles), and format it
// for display. Returns null when no PRIMA role is present.
// --------------------------------------------------------------------------
export function pickPrimaRole(resourceRoles: string[] | undefined | null): AppRole | null {
  if (!resourceRoles || resourceRoles.length === 0) return null;
  return ROLE_PRIORITY.find((r) => resourceRoles.includes(r)) ?? null;
}

// Turn a role code (e.g. "prima_manager") into a display label ("Manager").
export function formatRoleName(role: string): string {
  return role
    .replace(/^prima_/i, '')
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

// --------------------------------------------------------------------------
// Helper: filter navigation menu based on visible modules
// --------------------------------------------------------------------------

interface NavItem {
  id: string;
  title: string;
  icon: string;
  active?: boolean;
}

interface NavSection {
  section: string;
  icon: string;
  items: NavItem[];
}

export function filterNavigationMenu(
  menu: NavSection[],
  visibleModules: string[]
): NavSection[] {
  const moduleSet = new Set(visibleModules);

  return menu
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => moduleSet.has(item.id)),
    }))
    .filter((section) => section.items.length > 0);
}

// --------------------------------------------------------------------------
// Helper: override module actions based on role permissions
// --------------------------------------------------------------------------

export function applyRoleActions(
  modulesConfig: Record<string, any>,
  roleConfig: RoleMenuConfig
): Record<string, any> {
  const filtered: Record<string, any> = {};

  for (const moduleId of roleConfig.visibleModules) {
    if (modulesConfig[moduleId]) {
      filtered[moduleId] = {
        ...modulesConfig[moduleId],
        actions: roleConfig.moduleActions[moduleId] || [],
      };
    }
  }

  return filtered;
}

/**
 * Actions granted on a screen code that is NOT the one currently rendered.
 *
 * A screen normally receives its own actions through the `allowedActions`
 * prop that main_layout passes from MODULES_CONFIG. Drill-downs break that
 * one-to-one mapping: the Client master hosts the Registrations drill-down,
 * which is governed by the separate `client-registration` code so a manager
 * can record a GSTIN without being granted full client edit (fix 161).
 *
 * Reads the same localStorage blob main_layout reads, so it stays in step with
 * whatever login resolved. Returns [] when the module was not granted, when
 * nothing is stored yet, or when the blob will not parse — failing closed,
 * which for a UI gate means the row action simply does not render.
 */
export function getModuleActions(moduleId: string): string[] {
  try {
    const stored = localStorage.getItem('MODULES_CONFIG');
    if (!stored) return [];
    const parsed = JSON.parse(stored) as Record<string, { actions?: string[] }>;
    return parsed?.[moduleId]?.actions ?? [];
  } catch {
    return [];
  }
}
